import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  BlockOutlined,
  CallSplitOutlined,
  CurrencyRupeeRounded,
  EditOutlined,
  ErrorOutlineOutlined,
  EventOutlined,
  GroupsOutlined,
  HomeWorkOutlined,
  ListAltOutlined,
  PlaceOutlined,
  StarOutlineRounded,
  TagOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel, PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { ViewActionFooter } from "pages/PayRoll/PayInputs/Shared/ViewActionFooter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { esiEmployerCodeHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required, ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import {
  RecordHeaderCard,
  StatCardGrid,
  StatutoryList,
  SwitchLine,
  initialsOf,
} from "pages/PayRoll/Shared/statutoryUi";
import { ChoiceCard } from "pages/PayRoll/Shared/statutoryConfigUi";
import { ACTIVE_STATUS_COLORS, CODE_TYPE_LABELS, formatDay, shortCode } from "./esiConfigData";
import { dashedCode, deactivateEmployerCode, esiRequest, fetchCodeSites, fromApiCode, previewCodeLinks, saveEmployerCode, siteLabel } from "./esiConfigApi";

// Employer Codes tab (C4), Add / Edit ESI Code drawer (C4a) and ESI Code
// Details drawer (C4b), on the ESI API. Employees get their code from their
// site (Administration › Sites) on the last day of the month, so a code
// covers sites, never employees. The server checks the rules (17 digits,
// unique, one main code, a site in one active code at a time); the drawer
// only checks what's needed to send the form.

const ALL = "all";
const { purple, indigo, orange, pink, green, blue } = detailAccentPalette;

const emptyCode = (codes) => ({
  id: null,
  code: "",
  type: codes.some((code) => code.type === "MAIN") ? "SUB" : "MAIN",
  branch_name: "",
  coverage_from: moment().startOf("month").format("YYYY-MM-DD"),
  branch_office: "",
  address: "",
  site_ids: [],
  sites: [],
  status: "Active",
  row_version: null,
});

const validateCode = (form, codes) => {
  const errors = {};
  // Dashes, spaces and dots are allowed; the server keeps the digits.
  const code = String(form.code || "").replace(/[\s.-]/g, "");
  if (!code) errors.code = "Enter the code exactly as on the ESIC portal";
  else if (!/^\d{17}$/.test(code)) errors.code = "Enter the 17-digit code exactly as on the ESIC portal";
  else if (codes.some((item) => item.id !== form.id && item.code === code)) errors.code = "This code is already added";
  if (form.type === "MAIN" && codes.some((item) => item.id !== form.id && item.type === "MAIN")) errors.type = "A main code is already added";
  if (form.type === "SUB" && !codes.some((item) => item.type === "MAIN")) errors.type = "Add the main code first";
  if (!String(form.branch_name || "").trim()) errors.branch_name = "Enter the branch name";
  if (!form.coverage_from) errors.coverage_from = "Pick the coverage date";
  if (form.status === "Active" && !form.site_ids.length) errors.sites = "Pick at least one site";
  return errors;
};

// The main code's short name (e.g. "Erode Plant").
const mainName = (main) => String(main?.branch_name || "").replace(/^.*—\s*/, "");

// ---------------- Sites covered table ----------------

const siteHeadSx = { ...designType.captionText, fontWeight: designWeights.heading, color: designColors.slate600, py: 1, bgcolor: designColors.slateBg };
const siteCellSx = { ...designType.tableBodyCell, py: 1 };

// One row per site, in the API's order. Rows the API marks disabled (in
// another code, or inactive and not ticked) are grey and can't change.
const SitesTable = ({ sites, value, onChange, error }) => {
  const picked = new Set(value);
  const toggle = (id) => onChange(picked.has(id) ? value.filter((item) => item !== id) : [...value, id]);
  const message = (text) => (
    <Mui.TableRow>
      <Mui.TableCell colSpan={3} sx={{ ...siteCellSx, color: designColors.slate600, textAlign: "center", py: 2 }}>
        {text}
      </Mui.TableCell>
    </Mui.TableRow>
  );
  return (
    <Mui.Box sx={{ gridColumn: "1 / -1" }}>
      <Mui.Box sx={{ border: `1px solid ${error ? "#DC2626" : designColors.slate200}`, borderRadius: "10px", overflow: "hidden" }}>
        <Mui.Table size="small">
          <Mui.TableHead>
            <Mui.TableRow>
              <Mui.TableCell padding="checkbox" sx={siteHeadSx} />
              <Mui.TableCell sx={siteHeadSx}>Site</Mui.TableCell>
              <Mui.TableCell align="right" sx={{ ...siteHeadSx, width: 110 }}>
                Employees
              </Mui.TableCell>
            </Mui.TableRow>
          </Mui.TableHead>
          <Mui.TableBody>
            {sites == null
              ? message("Loading sites…")
              : !sites.length
                ? message("No sites yet. Add them in Administration › Sites.")
                : sites.map((site) => {
                    const checked = picked.has(site.id);
                    const grey = site.disabled || !site.is_active;
                    const note =
                      site.disabled_reason === "IN_OTHER_CODE" && site.in_code ? `In ${site.in_code.code_display}` : !site.is_active ? "Inactive" : "";
                    return (
                      <Mui.TableRow
                        key={site.id}
                        hover={!site.disabled}
                        onClick={site.disabled ? undefined : () => toggle(site.id)}
                        sx={{ cursor: site.disabled ? "default" : "pointer", "&:last-child td": { borderBottom: 0 } }}
                      >
                        <Mui.TableCell padding="checkbox">
                          <Mui.Checkbox
                            size="small"
                            checked={checked}
                            disabled={site.disabled}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => toggle(site.id)}
                            inputProps={{ "aria-label": siteLabel(site) }}
                            sx={{ "&.Mui-checked": { color: designColors.tealDark } }}
                          />
                        </Mui.TableCell>
                        <Mui.TableCell sx={siteCellSx}>
                          <Mui.Typography sx={{ ...designType.tableBodyCell, color: grey ? designColors.slate400 : designColors.slate900 }}>
                            {site.name}
                            {site.code ? (
                              <Mui.Box component="span" sx={{ color: designColors.slate400, ml: 0.75, fontSize: 12 }}>
                                {site.code}
                              </Mui.Box>
                            ) : null}
                          </Mui.Typography>
                          {note ? <Mui.Typography sx={{ ...designType.captionText, color: designColors.slate600 }}>{note}</Mui.Typography> : null}
                        </Mui.TableCell>
                        <Mui.TableCell align="right" sx={{ ...siteCellSx, color: grey ? designColors.slate400 : designColors.slate900 }}>
                          {site.employees.toLocaleString("en-IN")}
                        </Mui.TableCell>
                      </Mui.TableRow>
                    );
                  })}
          </Mui.TableBody>
        </Mui.Table>
      </Mui.Box>
      {error ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.5 }}>{error}</Mui.Typography> : null}
    </Mui.Box>
  );
};

// ---------------- Add / Edit drawer ----------------

const CodePanel = ({ open, initial, codes, domain, companyId, companyName, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [sites, setSites] = useState(null);
  const [hint, setHint] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm(initial);
    setShowErrors(false);
    setServerErrors({});
    setHint("");
  }, [open, initial]);

  // The Sites covered table. Editing sends code_id, so this code's sites
  // come back ticked; the ticks then follow the API.
  const codeId = initial?.id;
  useEffect(() => {
    if (!open) return undefined;
    setSites(null);
    const request = new AbortController();
    fetchCodeSites({ domain, companyId, codeId: codeId ?? undefined, signal: request.signal })
      .then((rows) => {
        setSites(rows);
        if (codeId != null) setForm((current) => (current ? { ...current, site_ids: rows.filter((row) => row.checked).map((row) => row.id) } : current));
      })
      .catch((err) => {
        if (err.name !== "AbortError") setSites([]);
      });
    return () => request.abort();
  }, [open, codeId, domain, companyId]);

  // "205 employees of this company work at these sites and will use this code from …"
  const siteKey = (form?.site_ids || []).join(",");
  useEffect(() => {
    if (!open || !siteKey) {
      setHint("");
      return undefined;
    }
    const request = new AbortController();
    const timer = setTimeout(() => {
      previewCodeLinks({ domain, companyId, codeId: codeId ?? undefined, siteIds: siteKey.split(","), signal: request.signal })
        .then((data) => setHint(data?.message || ""))
        .catch(() => {});
    }, 300);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [open, siteKey, codeId, domain, companyId]);

  if (!form) return null;
  const editing = form.id != null;
  const mainCode = codes.find((code) => code.type === "MAIN");
  const mainTaken = Boolean(mainCode && mainCode.id !== form.id);
  const errors = validateCode(form, codes);
  const error = (key) => (showErrors ? errors[key] || serverErrors[key] : undefined);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length) {
      // Say why nothing was saved (the field may be scrolled out of view).
      showToast("error", errors.type || Object.values(errors)[0]);
      return;
    }
    setSaving(true);
    setServerErrors({});
    try {
      const { code, message } = await saveEmployerCode({ domain, companyId, form });
      showToast("success", message || (editing ? `${code.code_display} updated.` : `${code.code_display} added.`));
      onSaved();
    } catch (err) {
      setServerErrors(err.fieldErrors || {});
      // An unknown or deleted site: reload the table.
      if (/pick sites from the list/i.test(String(err.fieldErrors?.sites || ""))) {
        fetchCodeSites({ domain, companyId, codeId: codeId ?? undefined })
          .then(setSites)
          .catch(() => {});
      }
      showToast("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={editing ? "Edit ESI Code" : "Add ESI Code"}
      subtitle={companyName}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save" primaryProps={{ onClick: save, loading: saving }} />}
    >
      <Mui.Stack spacing={2}>
        <FormSection title="Code Type" info={editing ? "The code type can't be changed after it is added." : "One main code per company; branches use sub-codes under it."}>
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ gridColumn: "1 / -1" }}>
            <ChoiceCard
              label="Main code"
              hint={mainTaken ? "already added" : undefined}
              selected={form.type === "MAIN"}
              disabled={mainTaken || editing}
              onClick={() => set("type")("MAIN")}
            />
            <ChoiceCard
              label="Branch sub-code"
              hint={!mainCode ? "needs a main code" : undefined}
              selected={form.type === "SUB"}
              // Selectable even before a main code exists; Save says why it
              // can't be added yet.
              disabled={editing && form.type === "MAIN"}
              onClick={() => set("type")("SUB")}
            />
          </Mui.Stack>
          {error("type") ? (
            <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", gridColumn: "1 / -1" }}>{error("type")}</Mui.Typography>
          ) : form.type === "SUB" && !mainCode ? (
            <Mui.Typography sx={{ ...designType.captionText, color: "#B54708", gridColumn: "1 / -1" }}>
              No main code yet. Add the company's main code first, then its branch sub-codes.
            </Mui.Typography>
          ) : null}
        </FormSection>

        <FormSection title="Code Details" info="Enter the code exactly as on the ESIC portal.">
          {/* Sub-code: the main code it sits under (read-only), then the sub-code.
              Main code: the code itself is the main code. */}
          {form.type === "SUB" ? (
            <Field wide label="Main code" helper="The company's main ESI code. Sub-codes sit under it.">
              <TextField
                value={mainCode ? `${mainCode.code_display || mainCode.code} — ${mainName(mainCode)}` : ""}
                placeholder="No main code yet — add the main code first"
                onChange={() => {}}
                disabled
              />
            </Field>
          ) : null}
          <Field
            wide
            label={
              <>
                {form.type === "SUB" ? "ESI sub-code" : "Main code"}
                <Required />
                <Mui.Box component="span" sx={{ ...designType.captionText, ml: 0.5 }}>(17 digits)</Mui.Box>
              </>
            }
            helper={form.type === "SUB" ? "This branch's code, exactly as on the ESIC portal. Dashes are fine." : "The company's main ESI code, exactly as on the ESIC portal. Dashes are fine."}
            error={error("code")}
          >
            <TextField
              value={form.code}
              onChange={(value) => set("code")(value.replace(/[^\d\s.-]/g, "").slice(0, 24))}
              error={error("code")}
              placeholder="51-00-123456-000-0001"
            />
          </Field>
          <Field wide label={<>Branch name<Required /></>} helper="Name shown in lists, files and challans." error={error("branch_name")}>
            <TextField value={form.branch_name} onChange={set("branch_name")} error={error("branch_name")} placeholder="e.g. Coimbatore Depot" />
          </Field>
          <Field label={<>Coverage from<Required /></>} helper="Date ESIC coverage started." error={error("coverage_from")}>
            <DateField value={form.coverage_from} onChange={set("coverage_from")} error={error("coverage_from")} />
          </Field>
          <Field label="ESIC branch office" helper="ESIC office for this code. Optional." error={error("branch_office")}>
            <TextField value={form.branch_office} onChange={set("branch_office")} placeholder="e.g. Coimbatore" />
          </Field>
          <Field wide label="Address" helper="Branch address as registered with ESIC. Optional." error={error("address")}>
            <TextField value={form.address} onChange={set("address")} placeholder="Branch address" />
          </Field>
        </FormSection>

        <FormSection title={<>Sites covered<Required /></>} info="Every employee of this company at these sites is filed under this code.">
          <SitesTable sites={sites} value={form.site_ids} onChange={set("site_ids")} error={error("sites")} />
          <Mui.Typography sx={{ ...designType.captionText, gridColumn: "1 / -1" }}>
            {hint ? `${hint} ` : ""}
            {"A site can be in only one ESI code at a time. Sites come from Administration › Sites."}
          </Mui.Typography>
        </FormSection>

        {/* Status: one line under the cards, as in the design. */}
        <Mui.Box sx={{ px: 0.5 }}>
          <Mui.Stack direction="row" alignItems="center" spacing={1}>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>Status</Mui.Typography>
            <SwitchLine checked={form.status === "Active"} onChange={(checked) => set("status")(checked ? "Active" : "Inactive")} label={form.status} />
          </Mui.Stack>
          <Mui.Typography sx={designType.captionText}>Inactive codes cover no sites and are left out of new ESIC files.</Mui.Typography>
        </Mui.Box>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---------------- Details drawer ----------------

const DetailsPanel = ({ open, code: row, domain, companyId, onClose, onEdit, onChanged }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  // Fresh details (covered employees, row_version) each time it opens.
  const rowId = row?.id;
  useEffect(() => {
    if (!open || rowId == null) return undefined;
    setDetail(null);
    const request = new AbortController();
    esiRequest({ domain, path: `config/employer-codes/${rowId}/`, query: { company_id: companyId }, signal: request.signal })
      .then((data) => setDetail(fromApiCode(data)))
      .catch(() => {});
    return () => request.abort();
  }, [open, rowId, domain, companyId]);

  if (!row) return null;
  const code = detail || row;
  const active = code.status === "Active";
  const covered = code.covered_employees ?? code.employees;
  const main = code.main_code;

  const deactivate = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const { message } = await deactivateEmployerCode({ domain, companyId, code });
      showToast("success", message || `${code.code_display} deactivated from next month.`);
      onChanged();
    } catch (err) {
      showToast("error", err.message);
      if (err.status === 409) onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="ESI Code Details"
      subtitle={CODE_TYPE_LABELS[code.type]}
      bodySx={panelBodySx}
      // Edit is a header icon, like the other view panels; Deactivate is the
      // full-width footer action (active codes only).
      headerActions={
        <PanelHeaderIconButton title={active ? "Edit" : "Edit & Activate"} onClick={() => onEdit(code)} sx={{ color: designColors.tealDark }}>
          <EditOutlined />
        </PanelHeaderIconButton>
      }
      footer={
        active ? (
          <ViewActionFooter recordAction={{ label: "Deactivate", icon: BlockOutlined, onClick: () => setConfirmOpen(true), disabled: busy }} />
        ) : null
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(mainName(code))}
          title={code.branch_name}
          chip={<RequestStatusChip status={code.status} colorCode={ACTIVE_STATUS_COLORS[code.status]} />}
          meta={code.type === "MAIN" ? `${code.code_display} · main code` : `${code.code_display} · under main code ${main?.code_display || shortCode(main?.code)}`}
          tone={active ? "teal" : "slate"}
        />
        <RequestDetailsSection
          title="CODE DETAILS"
          fields={[
            { icon: TagOutlined, label: code.type === "MAIN" ? "ESI code" : "ESI sub-code", value: code.code_display, accent: purple },
            ...(code.type === "SUB" && main ? [{ icon: StarOutlineRounded, label: "Main code", value: `${main.code_display} · ${mainName(main)}`, accent: indigo }] : []),
            { icon: EventOutlined, label: "Coverage from", value: formatDay(code.coverage_from), accent: orange },
            { icon: HomeWorkOutlined, label: "ESIC branch office", value: code.branch_office || "Not entered", accent: pink },
            ...(code.address ? [{ icon: PlaceOutlined, label: "Address", value: code.address, accent: blue, multiline: true }] : []),
            ...(!active && code.inactive_from ? [{ icon: EventOutlined, label: "Inactive from", value: formatDay(code.inactive_from), accent: orange }] : []),
          ]}
        />
        <RequestDetailsSection
          title="USED BY"
          fields={[
            {
              icon: PlaceOutlined,
              label: "Sites covered",
              value: code.sites.map((site) => `${siteLabel(site)}${site.is_active ? "" : " · inactive"}`).join(", ") || "None",
              accent: green,
              multiline: true,
            },
            { icon: GroupsOutlined, label: "Covered employees", value: Number(covered || 0).toLocaleString("en-IN"), accent: blue },
            { icon: TaskAltOutlined, label: "Last ESIC file", value: code.last_file || "No file yet", accent: green },
            { icon: CurrencyRupeeRounded, label: "Last challan", value: code.last_challan || "No challan yet", accent: orange },
          ]}
        />
        <RequestInfoBanner body="Deactivating a code stops new files from the next month. Past files and challans stay unchanged." />
      </Mui.Stack>
      <GlobalConfirmDialog
        open={confirmOpen}
        title="Deactivate code"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={deactivate}
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        maxWidth="sm"
        danger
      >
        {`Deactivate ${code.code_display} (${code.branch_name}) from the 1st of next month? No new ESIC files after that.${
          code.employees ? ` ${code.employees} employees at its sites will be flagged in Validation until their sites are in an active code.` : ""
        }`}
      </GlobalConfirmDialog>
    </GlobalSidePanel>
  );
};

// ---------------- Tab ----------------

// addRequest: bumped by the page header's "Add Code" button (top right, like
// the Team Workspace Approve screens) to open the Add Code panel.
export const EsiEmployerCodesTab = ({ domain, companyId, data, onReload, addRequest = 0 }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState(ALL);
  const [viewId, setViewId] = useState(null);
  const [panel, setPanel] = useState({ open: false, initial: null });

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "esi_employer_codes_saved_views",
    fullHeaders: esiEmployerCodeHeader,
  });

  const codes = data.codes;
  const mainCode = codes.find((code) => code.type === "MAIN");
  const allRows = useMemo(
    () =>
      // Main code first, its sub-codes under it.
      [...codes]
        .sort((a, b) => (a.type === b.type ? a.code.localeCompare(b.code) : a.type === "MAIN" ? -1 : 1))
        .map((code) => ({
          ...code,
          // The list shows, searches and exports the dashed code.
          code: code.code_display,
          type_label: CODE_TYPE_LABELS[code.type],
          // Main Code = the main code a sub-code sits under; the main code row has none ("—"), its code is in ESI Code.
          main_code_label: code.type === "MAIN" ? "—" : code.main_code?.code_display || mainCode?.code_display || "—",
          // Text for search and export; the cell shows chips.
          locations_label: code.sites.map((site) => (site.code ? `${site.name} · ${site.code}` : site.name)).join(", ") || "—",
        })),
    [codes, mainCode],
  );
  // Each header click opens a fresh Add Code panel. Clicks from before this
  // tab was opened (it unmounts on a tab switch) don't reopen it.
  const seenAddRequest = useRef(addRequest);
  useEffect(() => {
    if (addRequest === seenAddRequest.current) return;
    seenAddRequest.current = addRequest;
    setPanel({ open: true, initial: emptyCode(codes) });
    // Only a new click opens it, not a change of the code list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRequest]);

  const unlinked = data.unlinked;
  const rows = card === "MAIN" || card === "SUB" ? allRows.filter((row) => row.type === card) : allRows;

  const cards = [
    { key: ALL, title: "All codes", value: data.cards.all_codes ?? allRows.length, icon: ListAltOutlined, tone: "blue" },
    {
      key: "MAIN",
      title: "Main code",
      // The code itself (cards.main_code), dashed from the MAIN row.
      value: mainCode || data.cards.main_code ? (
        <Mui.Box component="span" sx={{ fontSize: 18, whiteSpace: "nowrap" }}>
          {mainCode?.code_display || dashedCode(data.cards.main_code)}
        </Mui.Box>
      ) : (
        "—"
      ),
      detail: mainCode ? mainCode.branch_name : "Not added yet",
      icon: StarOutlineRounded,
      tone: "purple",
    },
    { key: "SUB", title: "Branch sub-codes", value: data.cards.sub_codes ?? allRows.filter((row) => row.type === "SUB").length, icon: CallSplitOutlined, tone: "pink" },
    {
      key: "unlinked",
      title: "Sites not linked",
      value: data.cards.sites_not_linked ?? unlinked.length,
      icon: ErrorOutlineOutlined,
      tone: "red",
      filter: false,
    },
  ];

  const afterChange = () => {
    setPanel((current) => ({ ...current, open: false }));
    setViewId(null);
    onReload();
  };

  const renderCell = (row, key) => {
    switch (key) {
      case "code":
        return row.type === "MAIN" ? (
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.heading }}>{row.code_display}</Mui.Typography>
        ) : (
          <Mui.Stack direction="row" spacing={0.75} alignItems="center">
            <Mui.Box component="span" sx={{ color: designColors.slate400 }}>{"└"}</Mui.Box>
            <span>{row.code_display}</span>
          </Mui.Stack>
        );
      case "locations_label":
        return row.sites.length ? (
          <Mui.Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            {row.sites.map((site) => (
              <ToneChip key={site.id} label={site.code ? `${site.name} · ${site.code}` : site.name} tone={site.is_active ? "teal" : "slate"} />
            ))}
          </Mui.Stack>
        ) : (
          "—"
        );
      case "type_label":
        return <ToneChip label={row.type_label} tone={row.type === "MAIN" ? "blue" : "purple"} />;
      case "coverage_from":
        return formatDay(row.coverage_from);
      case "employees":
        return Number(row.employees || 0).toLocaleString("en-IN");
      case "status":
        return <RequestStatusChip status={row.status} colorCode={ACTIVE_STATUS_COLORS[row.status]} />;
      default:
        return row[key] || "—";
    }
  };

  const viewed = codes.find((code) => code.id === viewId);

  return (
    <>
      <StatCardGrid cards={cards} value={card} onChange={setCard} />
      <StatutoryList
        title="ESI Employer Codes"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="codes"
        searchPlaceholder="Search code, branch or site"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Stack spacing={1.5} sx={{ mt: 2 }}>
        {unlinked.map((site) => (
          <RequestInfoBanner key={site.id} tone="warning" body={`Site '${site.label}' (${site.employees} employees) is not linked to any ESI code.`} />
        ))}
        <RequestInfoBanner body="One ESIC file and one challan per code each month. A company with only a main code files everything under it." />
      </Mui.Stack>

      <DetailsPanel
        open={Boolean(viewed) && !panel.open}
        code={viewed}
        domain={domain}
        companyId={companyId}
        onClose={() => setViewId(null)}
        onEdit={(code) => setPanel({ open: true, initial: { ...code, status: "Active" } })}
        onChanged={afterChange}
      />
      <CodePanel
        open={panel.open}
        initial={panel.initial}
        codes={codes}
        domain={domain}
        companyId={companyId}
        companyName={topbarCompanyName}
        onClose={() => setPanel((current) => ({ ...current, open: false }))}
        onSaved={afterChange}
      />
    </>
  );
};
